import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { getDefaultStore } from "jotai";
import { ShieldCheckIcon } from "lucide-react";
import { z } from "zod";
import { AuthPage, RegisterForm } from "@tubo/ui/components/auth";
import { useUiTranslation } from "@tubo/ui/i18n";
import { isAuthenticatedAtom, useAuthActions } from "@tubo/store";
import {
  useSysGetBeianNumbers,
  useSysGetRegistrationStatus,
} from "@tubo/api-models/hooks/system-config-api.hooks";
import type { LoginResp } from "@tubo/api-models/hooks/auth-api.schemas";
import { buildBeianSubFooterLinks } from "@client/lib/beian-links";
import { getLoginNavigationOptions } from "@client/lib/login-default-route";
import { AuthPreferencesMenu } from "@client/components/auth/auth-preferences-menu";

const branding = {
  logo: "/TuboData-Logo-Light.png",
  brandingBackground: "linear-gradient(150deg, #001a33 0%, #003366 35%, #0055aa 65%, #007aff 100%)",
  footerLinksOnBranding: false,
};

const registerSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/register")({
  validateSearch: registerSearchSchema,
  beforeLoad: ({ search }) => {
    if (getDefaultStore().get(isAuthenticatedAtom)) {
      throw redirect(getLoginNavigationOptions(search.redirect ?? "/"));
    }
  },
  component: RegisterPage,
});

function RegisterPage() {
  const { t } = useUiTranslation("auth");
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/register" });
  const { setAuth } = useAuthActions();
  const { data: registrationData } = useSysGetRegistrationStatus();
  const registrationDisabled = registrationData?.allowed === false;
  const { data: beianData } = useSysGetBeianNumbers();
  const subFooterLinks = buildBeianSubFooterLinks(beianData);

  // 自助注册已关闭：直达 /register 时回退登录页
  useEffect(() => {
    if (registrationDisabled) {
      navigate({ to: "/login", search: { redirect }, replace: true });
    }
  }, [registrationDisabled, navigate, redirect]);

  if (registrationDisabled) {
    return null;
  }

  const handleSuccess = async (resp: LoginResp) => {
    // 设置密码成功后（无 token），跳转登录页
    if (!resp.token?.accessToken) {
      navigate({ to: "/login", search: { redirect } });
      return;
    }
    setAuth({ token: resp.token.accessToken, me: resp.me });
    // 身份边界 = 运行时边界：整页进入目标路由，上一会话的内存态
    // （Query 缓存/模块级 atom/blob 缓存等）随页面卸载全部蒸发
    window.location.assign(redirect ?? "/");
  };

  return (
    <AuthPage
      className="relative"
      branding={{
        ...branding,
        companyName: t("companyName"),
        logoAlt: t("logoAlt"),
        tagline: t("tagline"),
        footerText: t("copyright", {
          year: new Date().getFullYear(),
          company: t("companyName"),
        }),
        footerLinks: [
          {
            label: t("privacyPolicy"),
            href: "/privacy-policy",
            icon: <ShieldCheckIcon className="size-3.5" aria-hidden="true" />,
          },
        ],
        subFooterLinks,
      }}
    >
      <div className="absolute right-6 top-6 z-20 md:right-8 md:top-8">
        <AuthPreferencesMenu />
      </div>
      <RegisterForm
        onSuccess={handleSuccess}
        onToggleMode={() => navigate({ to: "/login", search: { redirect } })}
      />
    </AuthPage>
  );
}
